import React from 'react';
import { Box, Stack } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { NavLink, NavLinkProps } from 'react-router-dom';

export interface SideNavigationProps {
  children: React.ReactNode;
}

export interface SideNavigationItemProps
  extends Omit<NavLinkProps, 'children' | 'className' | 'style' | 'end'> {
  children: React.ReactNode;
  isDefault?: boolean;
}

const navigationStyles = {
  flexShrink: 0,
  mr: 3,
  width: 150,
};

const listStyles = {
  gap: 0.5,
  m: 0,
  p: 0,
};

const itemStyles = {
  listStyle: 'none',
};

const StyledNavLink = styled(NavLink)(
  ({ theme }) => css`
    box-sizing: border-box;
    display: block;
    overflow: hidden;
    padding: 6px 12px;
    border-radius: 7px;
    color: ${theme.palette.navItem.normal};
    font-size: 1rem;
    font-weight: 500;
    text-decoration: none;
    text-overflow: ellipsis;
    transition: background-color 140ms ease, color 140ms ease;
    white-space: nowrap;

    &:hover {
      background-color: ${theme.palette.navItem.hoverBackground};
      color: ${theme.palette.navItem.hover};
    }

    &:focus-visible {
      outline: 2px solid ${theme.palette.navItem.hover};
      outline-offset: 2px;
    }

    &.active {
      background-color: ${theme.palette.navItem.activeBackground};
      color: ${theme.palette.navItem.hover};
    }

    &.active:hover {
      background-color: ${theme.palette.navItem.activeBackground};
    }
  `
);

const SideNavigation = ({ children }: SideNavigationProps) => (
  <Box component="nav" sx={navigationStyles}>
    <Stack component="ul" sx={listStyles}>
      {children}
    </Stack>
  </Box>
);

const SideNavigationItem = React.memo(
  ({ children, isDefault = false, ...props }: SideNavigationItemProps) => (
    <Box component="li" sx={itemStyles}>
      <StyledNavLink {...props} end={isDefault}>
        {children}
      </StyledNavLink>
    </Box>
  )
);

SideNavigationItem.displayName = 'SideNavigationItem';

export { SideNavigation, SideNavigationItem };
